import DOMPurify from 'dompurify';

/**
 * ТЗ 0097 — чистая логика писем из ERP: HTML редактора → HTML письма,
 * подпись, шаблоны (plain) → HTML, показ HTML-писем и коды ошибок.
 *
 * Санитайзер СЕРВЕРА (`html_mail.sanitize_email_html`) — единственный фильтр
 * на входе в базу и в письмо. Здесь DOMPurify — второй рубеж при ПОКАЗЕ:
 * HTML входящего письма уже санитизирован сервером, но рисуется он в
 * браузере сотрудника, и доверять одному слою незачем.
 */

/**
 * Подсказка предела вложений на письмо, МБ. Настоящий предел — runtime-
 * настройка сервера `EMAIL_ATTACHMENTS_MAX_MB` (по умолчанию 18, 1–25), и
 * обычным ролям её не прочитать. Поэтому клиент НЕ блокирует отправку по
 * сумме: он предупреждает, а отказ (`attachment_ids` / `TOO_LARGE`) — за
 * сервером. Жёсткий клиентский потолок — только 25 МБ на файл (больше
 * сервер не примет ни при какой настройке).
 */
export const EMAIL_ATTACHMENTS_HINT_MB = 18;
export const EMAIL_ATTACHMENTS_HINT_BYTES =
  EMAIL_ATTACHMENTS_HINT_MB * 1024 * 1024;
export const EMAIL_FILE_HARD_MAX_BYTES = 25 * 1024 * 1024;

const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => ESCAPES[char]);

const URL_RE = /https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/g;

/**
 * Текст шаблона (plain) → HTML для редактора: строка — абзац, ссылки
 * кликабельны. Подстановки (`{first_name}`, `{link}`) остаются как есть —
 * их заполнит сервер (`{link}` в тексте он сам превратит в ссылку).
 */
export const plainToEmailHtml = (text: string): string => {
  const lines = (text || '').replace(/\r\n?/g, '\n').split('\n');
  return lines
    .map((line) => {
      if (!line.trim()) return '<p><br></p>';
      const escaped = escapeHtml(line).replace(
        URL_RE,
        (url) => `<a href="${url}">${url}</a>`,
      );
      return `<p>${escaped}</p>`;
    })
    .join('');
};

const BLOCK_END = /<\/(p|div|li|h[1-6]|blockquote|tr)>/gi;

/**
 * Видимый текст HTML (без разметки) — для проверок «пусто?», подстановок и
 * длины. Без DOM: регулярки достаточно, текст никуда не уходит.
 */
export const htmlToPlainText = (html: string): string =>
  (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(BLOCK_END, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/** Пустой ли HTML редактора (`<p><br></p>` — пустой). */
export const isRichEmpty = (html: string): boolean =>
  !htmlToPlainText(html).trim() && !/<img\b/i.test(html || '');

/**
 * Голый адрес в тексте: `http(s)://…`, `www.…` или почта. Фигурные скобки в
 * адрес не входят — подстановка `{link}` сразу за адресом остаётся
 * подстановкой (её заполнит сервер).
 */
const AUTOLINK_RE = /(?:https?:\/\/|www\.)[^\s<>"'{}]+|[\w.%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)*\.[a-z]{2,}/gi;
/** Знаки препинания, которые закрывают фразу, а не адрес. */
const AUTOLINK_TRAILING = /[.,;:!?'"»\]]$/;
/** Предыдущий символ, после которого `www.` — продолжение слова, не адрес. */
const AUTOLINK_GLUED = /[\w./@-]/;
/** Текст внутри этих элементов не трогаем: ссылка в ссылке, стили, код. */
const AUTOLINK_SKIP = new Set(['A', 'STYLE', 'SCRIPT', 'TITLE', 'TEXTAREA']);

/** Срезать с конца адреса пунктуацию фразы и непарную `)`. */
const trimLinkTail = (raw: string): string => {
  let url = raw;
  for (;;) {
    if (AUTOLINK_TRAILING.test(url)) {
      url = url.slice(0, -1);
    } else if (
      url.endsWith(')') &&
      (url.match(/\)/g) || []).length > (url.match(/\(/g) || []).length
    ) {
      url = url.slice(0, -1);
    } else {
      return url;
    }
  }
};

const linkHref = (text: string): string => {
  if (/^https?:\/\//i.test(text)) return text;
  if (/^www\./i.test(text)) return `https://${text}`;
  return `mailto:${text}`;
};

/** Разбить текстовый узел на текст и `<a>`; `null` — ссылок в нём нет. */
const linkifyTextNode = (node: Text): DocumentFragment | null => {
  const text = node.data;
  const doc = node.ownerDocument;
  const fragment = doc.createDocumentFragment();
  let cursor = 0;
  let found = false;
  AUTOLINK_RE.lastIndex = 0;
  for (
    let match = AUTOLINK_RE.exec(text);
    match;
    match = AUTOLINK_RE.exec(text)
  ) {
    const start = match.index;
    const isWww = /^www\./i.test(match[0]);
    const glued = start > 0 && AUTOLINK_GLUED.test(text[start - 1]);
    const value = trimLinkTail(match[0]);
    const isEmail = !/^(?:https?:\/\/|www\.)/i.test(value);
    // `www.` внутри слова и почта, приклеенная к адресу, — не ссылки.
    const valid =
      !(isWww && glued) &&
      !(isEmail && glued) &&
      (isEmail
        ? value.includes('@')
        : value.replace(/^(?:https?:\/\/|www\.)/i, '').length > 0);
    if (valid) {
      fragment.appendChild(doc.createTextNode(text.slice(cursor, start)));
      const anchor = doc.createElement('a');
      anchor.setAttribute('href', linkHref(value));
      anchor.setAttribute('target', '_blank');
      anchor.setAttribute('rel', 'noopener noreferrer');
      anchor.textContent = value;
      fragment.appendChild(anchor);
      cursor = start + value.length;
      found = true;
    }
    AUTOLINK_RE.lastIndex = start + Math.max(value.length, 1);
  }
  if (!found) return null;
  fragment.appendChild(doc.createTextNode(text.slice(cursor)));
  return fragment;
};

/**
 * Голые адреса и почта в ТЕКСТЕ письма → ссылки (`target="_blank"`,
 * `rel="noopener noreferrer"`). Разбор — DOM-парсером: регулярка по HTML
 * задела бы атрибуты (`src="https://…"`) и текст уже готовых `<a>`. Если
 * кандидатов нет, HTML возвращается как был — без пересериализации.
 */
export const linkifyHtml = (html: string): string => {
  if (!html || typeof DOMParser === 'undefined') return html;
  AUTOLINK_RE.lastIndex = 0;
  if (!AUTOLINK_RE.test(htmlToPlainText(html))) return html;
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
  const texts: Text[] = [];
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    let skip = false;
    for (
      let parent = node.parentElement;
      parent && parent !== doc.body;
      parent = parent.parentElement
    ) {
      if (AUTOLINK_SKIP.has(parent.tagName)) {
        skip = true;
        break;
      }
    }
    if (!skip) texts.push(node as Text);
  }
  let changed = false;
  texts.forEach((node) => {
    const fragment = linkifyTextNode(node);
    if (fragment && node.parentNode) {
      node.parentNode.replaceChild(fragment, node);
      changed = true;
    }
  });
  return changed ? doc.body.innerHTML : html;
};

const QUOTE_STYLE =
  'margin:0 0 0 0.8ex;border-left:1px solid #ccc;padding-left:1ex';

const QL_INDENT_RE = /(?:^|\s)ql-indent-(\d+)(?=\s|$)/;

const isListElement = (node: Node | null): node is HTMLElement =>
  Boolean(node) &&
  node?.nodeType === 1 &&
  /^(UL|OL)$/.test((node as Element).tagName);

interface IFlatListItem {
  tag: string;
  level: number;
  li: HTMLElement;
}

/** Плоская серия списков Quill → вложенные `<ul>/<ol>` (на месте). */
const nestListRun = (doc: Document, run: HTMLElement[]): void => {
  const items: IFlatListItem[] = [];
  run.forEach((list) => {
    Array.from(list.children).forEach((child) => {
      const li = child as HTMLElement;
      const match = (li.getAttribute('class') || '').match(QL_INDENT_RE);
      const level = match ? Number(match[1]) : 0;
      if (match) li.classList.remove(`ql-indent-${match[1]}`);
      if (li.hasAttribute('class') && !li.getAttribute('class')?.trim()) {
        li.removeAttribute('class');
      }
      items.push({ tag: list.tagName.toLowerCase(), level, li });
    });
  });
  const anchor = run[0];
  const parent = anchor.parentNode;
  if (!parent) return;
  const stack: Array<{ level: number; list: HTMLElement }> = [];
  items.forEach((item) => {
    while (stack.length && stack[stack.length - 1].level > item.level) {
      stack.pop();
    }
    let top = stack[stack.length - 1];
    if (
      top &&
      top.level === item.level &&
      top.list.tagName !== item.tag.toUpperCase()
    ) {
      stack.pop();
      top = stack[stack.length - 1];
    }
    if (!top || top.level < item.level) {
      const list = doc.createElement(item.tag);
      if (top) {
        let host = top.list.lastElementChild as HTMLElement | null;
        if (!host) {
          host = doc.createElement('li');
          top.list.appendChild(host);
        }
        host.appendChild(list);
      } else {
        parent.insertBefore(list, anchor);
      }
      top = { level: item.level, list };
      stack.push(top);
    }
    top.list.appendChild(item.li);
  });
  run.forEach((list) => list.parentNode?.removeChild(list));
};

/**
 * ТЗ 0112 — вложенные списки Quill. Quill 1.x хранит вложенность плоско:
 * `<li class="ql-indent-1">` в том же списке, а при смене вида (номера ↔
 * точки) — соседним списком. Сервер вырезает классы `ql-*`, и в письме
 * вложенность пропадала. Здесь серия соседних списков пересобирается во
 * вложенные `<ul>/<ol>`, которые понимает любой почтовый клиент.
 */
export const nestQuillLists = (html: string): string => {
  if (!html || !/ql-indent-\d/.test(html) || typeof DOMParser === 'undefined') {
    return html;
  }
  const doc = new DOMParser().parseFromString(
    `<!doctype html><html><body>${html}</body></html>`,
    'text/html',
  );
  const lists = Array.from(doc.body.querySelectorAll('ul,ol')).filter(
    (list) => !list.parentElement?.closest('li'),
  ) as HTMLElement[];
  const runs: HTMLElement[][] = [];
  lists.forEach((list) => {
    const last = runs[runs.length - 1];
    const prev = list.previousSibling;
    if (last && isListElement(prev) && prev === last[last.length - 1]) {
      last.push(list);
    } else {
      runs.push([list]);
    }
  });
  runs.forEach((run) => {
    if (run.some((list) => list.querySelector('li[class*="ql-indent-"]'))) {
      nestListRun(doc, run);
    }
  });
  return doc.body.innerHTML;
};

/**
 * HTML редактора (Quill) → HTML письма. Абзацы `<p>` почтовые клиенты
 * рисуют с полями (письмо выглядит «через строку»), поэтому — `<div>`, как
 * у Gmail; цитата — со стилем Gmail; вложенные списки — настоящей
 * вложенностью (`nestQuillLists`). Прочие классы `ql-*` вырежет сервер.
 */
export const editorHtmlToEmailHtml = (html: string): string => {
  if (isRichEmpty(html)) return '';
  return linkifyHtml(
    nestQuillLists(html)
      .replace(/<p(\s[^>]*)?>/gi, (_m, attrs = '') => `<div${attrs}>`)
      .replace(/<\/p>/gi, '</div>')
      .replace(
        /<blockquote(\s[^>]*)?>/gi,
        `<blockquote style="${QUOTE_STYLE}">`,
      ),
  );
};

/**
 * Подпись держится ВНЕ содержимого Quill и приклеивается к письму здесь, при
 * отправке. Quill пересобирает HTML по своей модели документа: атрибуты и
 * разметку, которых нет в его форматах (таблицы, картинки, стили подписи из
 * Gmail), он выбрасывает — подпись внутри редактора ломалась бы при первой
 * же правке, и отличить её от текста письма было бы нечем.
 */
export const SIGNATURE_MARKER = 'erp_signature';

export const composeEmailHtml = (
  editorHtml: string,
  signatureHtml?: string | null,
): string => {
  const body = editorHtmlToEmailHtml(editorHtml);
  const signature = (signatureHtml || '').trim();
  if (!signature) return body;
  return `${body}<div><br></div><div class="${SIGNATURE_MARKER}">${signature}</div>`;
};

export interface IMailSignatureLike {
  mailboxEmail?: string | null;
  html: string;
}

/**
 * Подпись для ящика: своя для этого ящика, иначе по умолчанию
 * (`mailboxEmail = null`), иначе нет.
 */
export const pickSignature = <T extends IMailSignatureLike>(
  signatures: readonly T[],
  mailboxEmail?: string | null,
): T | null => {
  const box = (mailboxEmail || '').trim().toLowerCase();
  const own = box
    ? signatures.find(
        (signature) => (signature.mailboxEmail || '').toLowerCase() === box,
      )
    : undefined;
  return own || signatures.find((signature) => !signature.mailboxEmail) || null;
};

/**
 * Схемы адресов, которые остаются в `href`/`src` письма: веб-ссылки, почта,
 * телефон, картинки `cid:` (их подменит {@link resolveCidImages}) и только
 * растровые `data:image/*` — ссылка на `data:image/svg+xml` несёт разметку и
 * сюда не проходит (в `src` картинки DOMPurify пускает `data:` сам: `<img>`
 * скриптов не исполняет). Относительные адреса и якоря в письме смысла не
 * имеют.
 */
const EMAIL_URI_RE = /^(?:(?:https?|mailto|tel|cid):|data:image\/(?:png|jpe?g|gif|webp)[;,])/i;

/**
 * HTML письма для показа: DOMPurify поверх серверного санитайзера.
 *
 * `<style>` разрешён: сервер (tinycss2) оставляет во входящем письме один
 * очищенный блок стилей в начале, без него вёрстка писем рассыпается.
 * `FORCE_BODY` — иначе ведущий `<style>` парсер унесёт в `<head>`, и
 * DOMPurify его потеряет. Стили живут только внутри песочницы-фрейма.
 */
export const sanitizeEmailHtml = (html: string): string =>
  DOMPurify.sanitize(html || '', {
    ADD_TAGS: ['style'],
    FORBID_TAGS: [
      'script',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'button',
      'textarea',
      'select',
      'link',
      'meta',
      'base',
    ],
    FORBID_ATTR: ['srcset', 'action', 'formaction'],
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: EMAIL_URI_RE,
    FORCE_BODY: true,
  });

/** Картинка `cid:` письма: подписанный адрес приватного S3 (живёт час). */
export interface IInlineImage {
  cid: string;
  url: string;
}

/**
 * Ключ `cid:` для сравнения — как его отдаёт сервер: без `<>`, нижний
 * регистр, URL-декодированный (`cid:logo%40x` = `logo@x`).
 */
export const normalizeCid = (raw: string): string => {
  let value = (raw || '').trim().replace(/^cid:/i, '');
  try {
    value = decodeURIComponent(value);
  } catch (e) {
    // Битая %-последовательность — сравниваем как есть.
  }
  return value.trim().replace(/^<|>$/g, '').trim().toLowerCase();
};

/** Прозрачный пиксель вместо картинки, адрес которой не пришёл. */
export const TRANSPARENT_PIXEL =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

const CID_SRC_RE = /(\ssrc\s*=\s*)(?:"\s*(cid:[^"]*)"|'\s*(cid:[^']*)'|(cid:[^\s>"'`=<]+))/gi;

/**
 * `url(cid:X)` в CSS письма — в `<style>` и в атрибуте `style` (фон
 * таблицы, баннер). Кавычки — `'`, `"` или их HTML-сущность в атрибуте.
 */
const CID_CSS_RE = /url\(\s*(?:"|'|&quot;|&#39;|&#x27;)?\s*(cid:[^\s"')<>&]+)\s*(?:"|'|&quot;|&#39;|&#x27;)?\s*\)/gi;

/** `cid:`, на которые ссылается письмо: `src` и CSS `url()` (нормализованные). */
export const referencedCids = (html?: string | null): Set<string> => {
  const found = new Set<string>();
  const source = html || '';
  source.replace(CID_SRC_RE, (_m, _p, dq, sq, bare) => {
    found.add(normalizeCid(dq ?? sq ?? bare ?? ''));
    return '';
  });
  source.replace(CID_CSS_RE, (_m, cid) => {
    found.add(normalizeCid(cid));
    return '';
  });
  return found;
};

/**
 * Адрес для CSS `url()` без кавычек: подставляется и в `<style>` (там
 * сущности не раскрываются — `&` остаётся как есть), и в атрибут `style`
 * в любых кавычках. Символы, ломающие `url()`/атрибут/`<style>`, — в %-код.
 */
const cssUrl = (url: string): string =>
  url.replace(
    /[\s"'()\\<>]/g,
    (ch) => `%${ch.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`,
  );

/**
 * `src="cid:X"` и CSS `url(cid:X)` → подписанный адрес картинки. Не нашли
 * адрес (картинка не растровая, вложение недоступно, ответ ещё не пришёл) —
 * прозрачный пиксель, а не значок битой картинки.
 */
export const resolveCidImages = (
  html: string,
  images?: readonly IInlineImage[] | null,
): string => {
  if (!html || !/cid:/i.test(html)) return html || '';
  const byCid = new Map<string, string>();
  (images || []).forEach((image) => {
    if (image.url) byCid.set(normalizeCid(image.cid), image.url);
  });
  return html
    .replace(CID_SRC_RE, (_m, prefix, dq, sq, bare) => {
      const url = byCid.get(normalizeCid(dq ?? sq ?? bare ?? ''));
      return `${prefix}"${escapeHtml(url || TRANSPARENT_PIXEL)}"`;
    })
    .replace(CID_CSS_RE, (_m, cid) => {
      const url = byCid.get(normalizeCid(cid));
      return `url(${cssUrl(url || TRANSPARENT_PIXEL)})`;
    });
};

/** Есть ли в HTML письма картинки `cid:` — тогда нужен запрос адресов. */
export const hasCidImages = (html?: string | null): boolean =>
  referencedCids(html).size > 0;

export interface IInlineAttachmentLike {
  contentId?: string | null;
  inline?: boolean | null;
  mime?: string | null;
}

/**
 * Типы картинок, которые сервер подставляет в HTML по `cid:`
 * (`messageInlineImages` — только png/jpeg/gif/webp, `_IMAGE_EXT` бэкенда).
 */
export const INLINE_SERVED_IMAGE_MIMES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
];

const isServedInlineImage = (mime?: string | null): boolean =>
  INLINE_SERVED_IMAGE_MIMES.includes(
    (mime || '').split(';', 1)[0].trim().toLowerCase(),
  );

/**
 * Вложения под письмом: встроенные (`inline`) части, которые HTML уже
 * показывает картинкой по `cid:`, в списке не дублируем. Встроенная часть,
 * на которую HTML не ссылается, остаётся — иначе её не открыть никак.
 *
 * Перепроверка F38: прячем только те, что сервер действительно отдаёт
 * картинкой (`INLINE_SERVED_IMAGE_MIMES`). PDF, HEIC, TIFF, SVG с `cid:` в
 * HTML не показываются (на их месте прозрачный пиксель) — без чипа их было
 * бы не открыть вовсе.
 */
export const visibleAttachments = <T extends IInlineAttachmentLike>(
  attachments: readonly T[] | null | undefined,
  bodyHtml?: string | null,
): T[] => {
  const list = attachments ? [...attachments] : [];
  const cids = referencedCids(bodyHtml);
  if (cids.size === 0) return list;
  return list.filter(
    (item) =>
      !(
        item.inline &&
        item.contentId &&
        isServedInlineImage(item.mime) &&
        cids.has(normalizeCid(item.contentId))
      ),
  );
};

/**
 * Умолчания фрейма — в `<head>`, ДО стилей письма: шрифт, цвет, поля
 * абзацев письмо вправе переопределить своим `<style>`.
 */
const FRAME_STYLE = `
body{font:14px/1.5 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:#0f172a;word-break:break-word;overflow-wrap:anywhere}
a{color:#2563eb}
blockquote{margin:0 0 0 0.8ex;border-left:1px solid #cbd5e1;padding-left:1ex;color:#475569}
p{margin:0 0 8px}
`;

/**
 * ТЗ 0112 — рамка фрейма — в КОНЦЕ `<body>`, ПОСЛЕ `<style>` письма: иначе
 * письмо своим `body{margin:…}` / `img{width:600px}` распирало фрейм и
 * обрезалось. Широкое содержимое (таблица-вёрстка) прокручивается по
 * горизонтали внутри фрейма, а не режется; `pre` переносится.
 */
const FRAME_BASE_STYLE = `
html{overflow-x:auto}
html,body{margin:0;padding:0;background:transparent}
img{max-width:100% !important;height:auto}
table{max-width:100%}
pre{white-space:pre-wrap;word-break:break-word}
`;

/**
 * CSP фрейма письма: ничего, кроме картинок по https/data (в том числе
 * подставленных вместо `cid:`), встроенных стилей и шрифтов по https.
 */
export const EMAIL_FRAME_CSP =
  "default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; " +
  "font-src https:; script-src 'none'; object-src 'none'";

/**
 * ТЗ 0122 — цвета темы для письма в тёмной теме. Фрейм не видит CSS-
 * переменных родителя, поэтому значения `--c-*` читаются в родителе
 * (`useEmailFrameTheme`) и вписываются в документ фрейма.
 */
export interface IEmailFrameDarkTheme {
  /** Основной текст (`--c-text`). */
  text: string;
  /** Вторичный текст — цитаты (`--c-text-2`). */
  textMuted: string;
  /** Ссылки (`--c-accent-text`). */
  link: string;
  /** Черта цитаты (`--c-border-strong`). */
  border: string;
}

/** Цвет из CSS-переменной — только «безопасные» символы (без `;{}<>`). */
const SAFE_CSS_COLOR = /^[#\w(),.%\s-]+$/;

export const safeCssColor = (value: string, fallback: string): string => {
  const trimmed = (value || '').trim();
  return trimmed && SAFE_CSS_COLOR.test(trimmed) ? trimmed : fallback;
};

/** Значения, которые цвета не задают: письмо от них «своим» не становится. */
const NEUTRAL_CSS_VALUE = /^\s*(inherit|initial|unset|currentcolor|transparent|none)\s*(!important)?\s*$/i;
/** `color:` / `background…:` в CSS (не `border-color`, не `text-decoration-color`). */
const CSS_COLOR_DECL = /(?:^|[^-\w])(color|background(?:-color|-image)?)\s*:\s*([^;}]*)/gi;
const STYLE_ATTR = /\bstyle\s*=\s*("[^"]*"|'[^']*')/gi;
const STYLE_BLOCK = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
/** Цвета атрибутами старой вёрстки: `bgcolor`, `background`, `<font color>`. */
const COLOR_ATTR = /<[a-z][^>]*\s(?:bgcolor|background)\s*=|<font\b[^>]*\scolor\s*=/i;

const cssSetsColors = (css: string): boolean => {
  CSS_COLOR_DECL.lastIndex = 0;
  for (
    let match = CSS_COLOR_DECL.exec(css);
    match;
    match = CSS_COLOR_DECL.exec(css)
  ) {
    if (!NEUTRAL_CSS_VALUE.test(match[2])) return true;
  }
  return false;
};

/**
 * ТЗ 0122 — «свёрстанное» письмо: задаёт свои цвета текста или фона
 * (рассылки, счета, подписи с цветом). Простое письмо (обычная переписка)
 * в тёмной теме рисуется цветами темы; свёрстанное — инверсией, иначе его
 * тёмный текст лёг бы на тёмный фон, а светлые плашки слепили бы.
 */
export const isDesignedEmail = (html: string): boolean => {
  const source = html || '';
  if (COLOR_ATTR.test(source)) return true;
  const chunks: string[] = [];
  STYLE_ATTR.lastIndex = 0;
  for (
    let match = STYLE_ATTR.exec(source);
    match;
    match = STYLE_ATTR.exec(source)
  ) {
    chunks.push(match[1].slice(1, -1));
  }
  STYLE_BLOCK.lastIndex = 0;
  for (
    let match = STYLE_BLOCK.exec(source);
    match;
    match = STYLE_BLOCK.exec(source)
  ) {
    chunks.push(match[1]);
  }
  return chunks.some(cssSetsColors);
};

/** Тёмная тема, простое письмо: цвета темы вместо светлых умолчаний. */
const darkSimpleStyle = (dark: IEmailFrameDarkTheme): string =>
  `body{color:${safeCssColor(dark.text, '#f7f7f8')}}` +
  `a{color:${safeCssColor(dark.link, '#4fadb6')}}` +
  `blockquote{border-left-color:${safeCssColor(
    dark.border,
    'rgba(247,247,248,0.22)',
  )};color:${safeCssColor(dark.textMuted, '#abb0b3')}}`;

/**
 * Тёмная тема, рамка: `color-scheme` в документе (и на самом `<iframe>`)
 * совпадает с родителем — иначе браузер подкладывает под прозрачный фрейм
 * непрозрачный белый холст.
 */
const FRAME_DARK_BASE_STYLE = ':root{color-scheme:dark}';

/** Картинки и фоны-картинки: их вернуть в исходные цвета. */
const DARK_MEDIA =
  'img,video,picture,svg,[background],[style*="background-image"],' +
  '[style*="url("]';

/**
 * Тёмная тема, свёрстанное письмо — известный приём «инверсия + поворот
 * оттенка на 180°»: светлый фон становится тёмным, тёмный текст — светлым,
 * цвета акцентов сохраняют оттенок. `invert(.9)` вместо `invert(1)` —
 * приглушение: белое → #1a1a1a, чёрный текст → #e6e6e6, без слепящего
 * контраста. Фото и логотипы инвертируются обратно (вложенные в уже
 * возвращённый блок — не трогаются, иначе инвертировались бы дважды).
 *
 * Фильтр — на `body` с белым фоном, а `<html>` — непрозрачный тёмный:
 * фон корня уходит на холст мимо фильтра, и прозрачный `<html>` отдал бы
 * холсту белый фон `body` неинвертированным.
 */
const FRAME_DARK_DESIGNED_STYLE =
  `${FRAME_DARK_BASE_STYLE}` +
  'html{background:#1a1a1a}' +
  'body{background:#fff;filter:invert(.9) hue-rotate(180deg)}' +
  `${DARK_MEDIA}{filter:invert(1) hue-rotate(180deg)}` +
  `:is(${DARK_MEDIA}) :is(${DARK_MEDIA}){filter:none}`;

/**
 * Документ для `<iframe srcDoc>`: CSP без скриптов (поверх `sandbox` без
 * `allow-scripts`), `<base target="_blank">` — ссылки в новой вкладке.
 *
 * ТЗ 0122: `dark` — тёмная тема ERP. Без него (светлая) документ прежний.
 */
export const buildEmailSrcDoc = (
  html: string,
  dark?: IEmailFrameDarkTheme | null,
): string => {
  const clean = sanitizeEmailHtml(html);
  if (!dark) {
    // Светлая тема — документ байт в байт как до ТЗ 0122.
    return (
      '<!doctype html><html><head><meta charset="utf-8">' +
      `<meta http-equiv="Content-Security-Policy" content="${EMAIL_FRAME_CSP}">` +
      `<base target="_blank"><style>${FRAME_STYLE}</style></head>` +
      `<body>${clean}<style>${FRAME_BASE_STYLE}</style>` +
      '</body></html>'
    );
  }
  const designed = isDesignedEmail(clean);
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<meta name="color-scheme" content="dark">' +
    `<meta http-equiv="Content-Security-Policy" content="${EMAIL_FRAME_CSP}">` +
    `<base target="_blank"><style>${FRAME_STYLE}${
      designed ? '' : darkSimpleStyle(dark)
    }</style></head>` +
    `<body>${clean}<style>${FRAME_BASE_STYLE}${
      designed ? FRAME_DARK_DESIGNED_STYLE : FRAME_DARK_BASE_STYLE
    }</style>` +
    '</body></html>'
  );
};

export interface IFileSizeUnits {
  b: string;
  kb: string;
  mb: string;
}

const oneDecimal = (value: number): string =>
  (Math.round(value * 10) / 10).toString().replace('.', ',');

/** Размер файла: `512 Б`, `14 КБ`, `3,4 МБ` (единицы — из перевода). */
export const formatFileSize = (
  bytes: number | null | undefined,
  units: IFileSizeUnits,
): string => {
  const size = Math.max(0, bytes || 0);
  if (size < 1024) return `${size} ${units.b}`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} ${units.kb}`;
  return `${oneDecimal(size / (1024 * 1024))} ${units.mb}`;
};

/** Мегабайты одним знаком после запятой — для счётчика «N / 25 МБ». */
export const formatMegabytes = (bytes: number): string =>
  oneDecimal(Math.max(0, bytes) / (1024 * 1024));

/** Коды ошибок ТЗ 0097, у которых есть перевод `richMail.errors.*`. */
export const RICH_MAIL_ERROR_CODES = [
  'NOT_YOUR_MAILBOX',
  'REQUIRED',
  'GMAIL_ERROR',
  'NO_SIGNATURE',
  'INVALID_FILE',
  'FILE_TOO_LARGE',
  'NOT_FOUND',
  'EXPIRED',
  'SIGNATURE_TOO_LARGE',
  'TOO_LARGE',
  'UNAVAILABLE',
] as const;
export type RichMailErrorCode = typeof RICH_MAIL_ERROR_CODES[number];

export interface IRichMailError {
  code: string;
  message: string;
}

/**
 * Отказ мутации подписи/вложения: `GraphQLError` с `extensions.code`
 * (Apollo кладёт его в `graphQLErrors`); сбой сети — без кода.
 */
export const richMailError = (caught: unknown): IRichMailError => {
  const error = caught as {
    graphQLErrors?: Array<{
      message?: string;
      extensions?: { code?: string | null } | null;
    }>;
    message?: string;
  } | null;
  const first = error?.graphQLErrors?.[0];
  return {
    code: String(first?.extensions?.code || ''),
    message: String(first?.message || error?.message || ''),
  };
};

/** i18n-ключ ошибки или `null` (незнакомый код — показываем текст сервера). */
export const richMailErrorKey = (code?: string | null): string | null => {
  const normalized = (code || '').toUpperCase();
  return (RICH_MAIL_ERROR_CODES as readonly string[]).includes(normalized)
    ? `richMail.errors.${normalized}`
    : null;
};

/** Отказ из-за 403 Gmail: нужен скоуп `gmail.settings.basic`. */
export const isGmailScopeError = (error: IRichMailError): boolean =>
  error.code === 'GMAIL_ERROR' && /settings\.basic|403/i.test(error.message);

/** Поле отказа отправки, которое касается вложений. */
export const ATTACHMENT_ERROR_FIELD = 'attachment_ids';

export const isAttachmentError = (error: { field?: string | null }): boolean =>
  (error.field || '').replace(/([A-Z])/g, '_$1').toLowerCase() ===
  ATTACHMENT_ERROR_FIELD;

/**
 * Отказ отправки письма, у которого есть слова ТЗ 0097: вложения
 * (`attachment_ids`: NOT_FOUND / EXPIRED / UNAVAILABLE / TOO_LARGE),
 * слишком большой HTML (`html` / TOO_LARGE, больше 2 МБ) и слишком глубоко
 * вложенная разметка (`html` / TOO_DEEP, перепроверка F36 — сервер не
 * разбирает дерево глубже 512 уровней). Иначе `null` — текст ошибки решает
 * вызывающий.
 */
export const richMailSendErrorKey = (error: {
  field?: string | null;
  code?: string | null;
}): string | null => {
  const field = (error.field || '').replace(/([A-Z])/g, '_$1').toLowerCase();
  const code = (error.code || '').toUpperCase();
  if (field === 'html' && code === 'TOO_LARGE') {
    return 'richMail.errors.HTML_TOO_LARGE';
  }
  if (field === 'html' && code === 'TOO_DEEP') {
    return 'richMail.errors.HTML_TOO_DEEP';
  }
  if (field === ATTACHMENT_ERROR_FIELD) return richMailErrorKey(code);
  return null;
};
